import { useOnlineStatus } from '@hooks/useOnlineStatus';
import { useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { useAppDispatch } from '@/store';
import { Divider, RoundIcon, Typography } from '@ecdlink/ui';
import { useWindowSize } from '@reach/window-size';
import { ConnectItem } from '@ecdlink/graphql';
import { communitySelectors, communityThunkActions } from '@/store/community';
import { ExternalLinkIcon } from '@heroicons/react/solid';

const HEADER_HEIGHT = 222;

interface ConnectChild {
  buttonText?: string;
  link?: string;
}

interface ConnectGroup {
  name: string;
  children: ConnectChild[];
}

export const Connect: React.FC = () => {
  const { isOnline } = useOnlineStatus();
  const dispatch = useAppDispatch();
  const { height } = useWindowSize();

  const connectData = useSelector(communitySelectors.getConnectData);
  const connectItemData = useSelector(communitySelectors.getConnectItems);

  // Load main connect categories
  useEffect(() => {
    if (isOnline && connectData?.length === 0) {
      dispatch(
        communityThunkActions.getAllConnect({
          locale: 'en-za',
        })
      );
    }
  }, [isOnline, dispatch, connectData?.length]);

  // Load connect items (links/resources)
  useEffect(() => {
    if (isOnline && connectItemData?.length === 0) {
      dispatch(
        communityThunkActions.getAllConnectItem({
          locale: 'en-za',
        })
      );
    }
  }, [isOnline, dispatch, connectItemData?.length]);

  const connectGroups = useMemo<ConnectGroup[]>(() => {
    if (!connectData?.length || !connectItemData?.length) {
      return [];
    }

    // Create lookup map: name → children[]
    const childrenByParent = new Map<string, ConnectChild[]>();

    connectItemData.forEach((item: ConnectItem) => {
      const parentName = item?.linkedConnect?.[0]?.name;
      if (!parentName) return;

      if (!childrenByParent.has(parentName)) {
        childrenByParent.set(parentName, []);
      }

      childrenByParent.get(parentName)!.push({
        buttonText: item.buttonText!,
        link: item.link!,
      });
    });

    // Map main categories → enriched groups
    return connectData.map((item) => ({
      name: item.name || 'Unnamed',
      children: childrenByParent.get(item.name || '') || [],
    }));
  }, [connectData, connectItemData]);

  const openLink = (url?: string) => {
    if (!url) return;
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  };

  return (
    <div
      className="flex flex-col p-4"
      style={{ height: height - HEADER_HEIGHT }}
    >
      <div className="flex items-center gap-3 rounded-2xl py-4">
        <RoundIcon icon="BookOpenIcon" backgroundColor="secondary" />
        <Typography type="h2" color="textDark" text="Links & resources" />
      </div>

      <Divider className="mb-4" dividerType="dashed" />

      {connectGroups.map((group, index) => (
        <div key={group.name}>
          <Typography
            type="h3"
            weight="bold"
            lineHeight="snug"
            color="textDark"
            className="mb-4"
            text={group.name}
          />

          {group.children.map((child, childIdx) => (
            <div
              key={child.link || childIdx}
              className="bg-uiBg text-textDark mb-2 flex items-center gap-1 rounded-2xl p-4 font-semibold"
            >
              <table className="w-full table-fixed border border-gray-100">
                <thead>
                  <tr>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="pr-4 align-middle">{child.buttonText}</td>
                    <td className="w-16 text-right align-middle">
                      <button
                        type="button"
                        onClick={() => openLink(child.link)}
                        className="focus:outline-none inline-flex items-center justify-center"
                      >
                        <ExternalLinkIcon className="text-primary h-6 w-6" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}

          {index < connectGroups.length - 1 && (
            <Divider className="my-4" dividerType="dashed" />
          )}
        </div>
      ))}
    </div>
  );
};

export default Connect;
